import { ChangeDetectorRef, Component, OnInit } from '@angular/core';

import { DxAccordionModule, DxCheckBoxModule, DxDataGridModule, DxTemplateModule } from 'devextreme-angular';

import { CommonModule } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';
import { DataService as CoreDataService, PersistMode, QueryColumn } from '@barthauer/abc/core/data';
import { _AbcButtonComponent, _AbcGridComponent, _AbcSelectBoxComponent } from '@barthauer/abc/ui-components';
import { of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { PickListLanguage, ToastType } from '../../models/enumeration-list.model';
import { AppService } from '../../services/app.service';
import { DataService, ObjectKeys } from '../../services/data.service';
import { NotificationService } from '../../services/notification.service';
import { PermessionService } from '../../services/permession.service';
import { UbtLoaderComponent } from '../../shared/ubt-loader/ubt-loader.component';
import { PermessionAccordionComponent } from '../permession-accordion/permession-accordion.component';
@Component({
  selector: 'app-user-settings',
  templateUrl: './user-settings.component.html',
  styleUrls: ['./user-settings.component.scss'],
  standalone: true,
  imports: [
    FlexLayoutModule,
    DxDataGridModule,
    DxCheckBoxModule,
    DxTemplateModule,
    _AbcGridComponent,
    CommonModule,
    _AbcButtonComponent,
    UbtLoaderComponent,
    DxAccordionModule,
    _AbcSelectBoxComponent,
    PermessionAccordionComponent,
  ],
})
export class UserSettingsComponent implements OnInit {
  public permissions = [];
  public objectKeys = ObjectKeys;
  public groupedModules = {
    administration: [],
    Measure: [],
    Subproject: [],
    otherModules: [],
  };
  public isOtherModule = false;
  dataSource = [
    { value: true, displayText: 'Yes' },
    { value: false, displayText: 'No' },
  ];
  public readonly pickListItemColumns: QueryColumn[] = [
    {
      OPath: 'Id',
      Name: 'Id',
    },
    {
      OPath: 'Description',
      Name: 'Description',
    },
    {
      OPath: 'Value',
      Name: 'Value',
    },
    {
      OPath: 'Picklist.Language',
      Name: 'Language',
    },

    {
      OPath: 'ParentId',
      Name: 'ParentId',
    },
  ];
  public PERMISSION_GRANTED = '1';
  public PERMISSION_DENIED = '0';
  public savedPermessions: Permession = new Permession(null);
  public columns: QueryColumn[] = [];
  public roleId: string;
  public roleValue: string;
  public showLoader: boolean = true;
  public previousSelectedModule: string;
  public arrayOfPermessions: Permession[] = [];
  public secondListPermessions: Permession[] = [];
  public translations = {
    access: this.dataService.res('See-Overview'),
    create: this.dataService.res('Create-Module'),
    update: this.dataService.res('Edit-Module'),
    delete: this.dataService.res('Delete-Module'),
    statusReport: this.dataService.res('Measure-Status-Report'),
    description: this.dataService.res('Measure-Description'),
    infos: this.dataService.res('Measure-infos'),
    mileStone: this.dataService.res('Measure-MileStones'),
    task: this.dataService.res('Measure-Tasks'),
  };
  public modulesLoaded: boolean = false;
  private subscriptions: Subscription = new Subscription();
  constructor(
    private cdr: ChangeDetectorRef,
    private coreDataService: CoreDataService,
    public dataService: DataService,
    public permessionService: PermessionService,
    public appService: AppService,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.loadPermissions();
  }

  public loadPermissions(): void {
    this.showLoader = true;
    const permissionSub = this.permessionService
      .getRole()
      .pipe(
        switchMap((role) => {
          this.roleId = role?.Id;
          this.roleValue = role?.Value;
          return this.roleId ? this.permessionService.getPermessions(this.roleId) : of([]);
        })
      )
      .subscribe(
        (permissions) => {
          this.transformPermissions(Array.isArray(permissions) ? permissions : []);
          this.organizeModules();
          this.showLoader = false;
          this.cdr.markForCheck();
        },
        (error) => {
          console.error('Error fetching permissions:', error);
          this.showLoader = false;
        }
      );
    this.subscriptions.add(permissionSub);
  }
  private organizeModules(): void {
    this.groupedModules.administration = [];
    this.groupedModules.Measure = [];
    this.groupedModules.otherModules = [];
    this.groupedModules.Subproject = [];
    this.permissions.forEach((module) => {
      if (['Roles', 'Companies', 'Stuff-Members', 'Documents', 'Project'].includes(module.module)) {
        this.groupedModules.administration.push(module);
      } else if (['Measure'].includes(module.module)) {
        this.groupedModules.Measure.push(module);
      } else if (['Subproject'].includes(module.module)) {
        this.groupedModules.Subproject.push(module);
      } else {
        this.groupedModules.otherModules.push(module);
      }
    });

    this.modulesLoaded = true;
  }
  public transformPermissions(permissions: Permession[]): void {
    this.permissions = permissions.filter((perm) => perm.permession != null);
    this.permissions.forEach((permession) => {
      permession.module = this.lastDashIndex(permession.permessionName);
    });
  }

  public lastDashIndex(permessionName: string): string {
    const lastDashIndex = permessionName.lastIndexOf('-');
    return lastDashIndex !== -1 ? permessionName.substring(0, lastDashIndex) : permessionName;
  }

  public checkPersistMode(permission: Permession): number {
    return permission.Id ? PersistMode.Update : PersistMode.Insert;
  }
  public saveChanges() {
    const permissionMap = new Map(this.arrayOfPermessions.map((permession) => [permession.permessionName, permession]));

    this.secondListPermessions = this.secondListPermessions
      .filter((permession) => permissionMap.has(permession.permessionName))
      .map((permession) => {
        const matchingPermession = permissionMap.get(permession.permessionName);
        return {
          ...permession,
          PermessionValue: matchingPermession!.PermessionValue,
          permession: matchingPermession!.permession,
        };
      });
    [...this.secondListPermessions, ...this.arrayOfPermessions].forEach((permission) => {
      this.savePermissions(permission);
    });
  }
  public verifyChanges(module): void {
    this.savedPermessions = module;
    const existingIndex = this.arrayOfPermessions.findIndex((perm) => perm.Id === this.savedPermessions.Id);

    if (existingIndex !== -1) {
      this.arrayOfPermessions.splice(existingIndex, 1);
    }
    this.arrayOfPermessions.push(this.savedPermessions);
    this.previousSelectedModule = module.module;
  }
  public onPermissionChange(updatedPermission): void {
    this.verifyChanges(updatedPermission);
  }
  public getOtherLanguagePermissionList() {
    const otherLanguage =
      sessionStorage.getItem('pickListLanguage') == PickListLanguage.German
        ? PickListLanguage.English
        : PickListLanguage.German;
    const pickListItemOpath = `Picklist.Language=${otherLanguage} AND Picklist.Name ='Personenposition' And Value='${this.roleValue}'`;

    this.dataService
      .readObjects(ObjectKeys.PICKLISTITEM, this.pickListItemColumns, pickListItemOpath)
      .pipe(
        switchMap((pickListData: any[]) => {
          if (Array.isArray(pickListData) && pickListData.length > 0) {
            return pickListData;
          }
          return of(null);
        }),
        switchMap((role) => {
          let roleId = role.Id;
          return this.roleId ? this.permessionService.getPermessions(roleId) : of([]);
        })
      )
      .subscribe({
        next: (permissions) => {
          this.secondListPermessions = permissions;
        },
        error: (error) => {
          console.error('Error fetching data:', error);
          this.showLoader = false;
        },
      });
  }

  public savePermissions(object: Permession) {
    const peristMode = this.checkPersistMode(object);
    this.permessionService.persistPermession(object, peristMode).subscribe(
      () => {
        this.notificationService.callNotification({
          message: this.dataService.res('Ubt-Permession-Update-Message'),
          type: ToastType.SUCCESS,
        });
      },
      (error) => {
        this.notificationService.callNotification({
          message: this.dataService.res('Ubt-Permession-Update-Error-Message'),
          type: ToastType.ERROR,
        });
      }
    );
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
export class Permession {
  Id: string;
  ParentId: string;
  module: string;
  permession: boolean;
  PermessionValue: boolean;
  permessionName: string;
  constructor(data) {
    Object.assign(this, data);
  }
}
